// KeySetupTabs — Account & Billing "advanced" key area (3 tabs).
//
// Jun 2026 (Randy) — Turns the single "Bring your own LLM keys" card into a
// tabbed system that mirrors the dashboard Operations tab's voucher/tracker
// systems (the same portfolio-wide pills, which "speak to each silo as
// children"):
//
//   • Tab 1 — Key Setup : the existing CreatorProviderByokPanel (instructions,
//     "How" walkthroughs + direct links, Add/Replace key per provider).
//   • Tab 2 — Vouchers   : the RED voucher pills for ALL three providers
//     (top-up + redistribute). Header note: only fund a voucher if you don't
//     plan to use BYOK direct savings.
//   • Tab 3 — Key Trackers : the GREEN BYOK balance pills for ALL three
//     providers. Each vendor is greyed + locked until that vendor's key is
//     connected; tapping a locked one pops "set up your key for this vendor".
//
// Reuses ProviderTrackerTile / VoucherTopupModal / RedistributePopup verbatim
// so this stays perfectly in sync with the dashboard + Operations trackers.
import { useState, useEffect, useCallback } from "react";
import { Key, Wallet, Gauge, Lock, ArrowRight, X, Info } from "@phosphor-icons/react";
import { getUser, api } from "@/lib/api";
import CreatorProviderByokPanel from "@/components/CreatorProviderByokPanel";
import VoucherTopupModal from "@/components/VoucherTopupModal";
import { ProviderTrackerTile, RedistributePopup, PROVIDER_META } from "@/components/ProviderTrackerCore";

const PROV_IDS = ["gemini", "anthropic", "openai"];

const TABS = [
  { id: "keys", label: "Key Setup", icon: Key },
  { id: "vouchers", label: "Vouchers", icon: Wallet },
  { id: "trackers", label: "Key Trackers", icon: Gauge },
];

// A greyed, locked green-tracker tile. Same footprint as ProviderTrackerTile
// but inert — tapping it pops the "connect this key first" hint.
function LockedTrackerTile({ provider, onClick }) {
  const meta = PROVIDER_META[provider];
  if (!meta) return null;
  return (
    <button
      type="button"
      onClick={() => onClick(provider)}
      data-testid={`tracker-locked-${provider}`}
      className="text-left border border-[#E2E0DC] bg-[#F4F3F1] rounded-md p-3 flex flex-col gap-2 opacity-80 hover:opacity-100 transition-opacity cursor-pointer"
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] uppercase tracking-[0.18em] text-[#8B8A87] font-medium">{meta.label}</span>
        <span className="text-[9px] uppercase tracking-[0.15em] px-1.5 py-0.5 rounded bg-[#EDECEA] text-[#8B8A87] inline-flex items-center gap-1">
          <Lock size={9} weight="duotone" /> Locked
        </span>
      </div>
      <div className="font-serif-display text-4xl leading-none tabular-nums text-[#C2C0BC]">—</div>
      <div className="text-[10px] text-[#9B9893] leading-snug">
        Connect your {meta.label} key to unlock this tracker.
      </div>
    </button>
  );
}

function LockedVendorModal({ provider, onClose, onGoSetup }) {
  const meta = PROVIDER_META[provider];
  if (!meta) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={onClose} data-testid="tracker-locked-modal">
      <div className="absolute inset-0 bg-[#1C1B1A]/40 backdrop-blur-sm" />
      <div className="modal-safe relative z-10 bg-white rounded-xl max-w-sm w-full p-6 shadow-2xl text-center" onClick={(e) => e.stopPropagation()}>
        <div className="w-12 h-12 rounded-full bg-[#F0F1EE] text-[#6B6A68] flex items-center justify-center mx-auto mb-3">
          <Lock size={22} weight="duotone" />
        </div>
        <h3 className="font-serif-display text-xl text-[#1C1B1A]">Set up your {meta.label} key first</h3>
        <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
          This tracker lights up the moment you connect your {meta.label} key. Head to the
          Key Setup tab to add it — it only takes about a minute.
        </p>
        <div className="mt-5 flex items-center justify-center gap-2">
          <button
            onClick={onGoSetup}
            data-testid="tracker-locked-go-setup"
            className="px-5 py-2.5 rounded-md bg-[#C16E5A] text-white hover:bg-[#A85E4A] text-sm flex items-center gap-2"
          >
            Go to Key Setup <ArrowRight size={14} weight="bold" />
          </button>
          <button onClick={onClose} data-testid="tracker-locked-close" className="px-4 py-2.5 text-sm text-[#6B6A68] hover:text-[#1C1B1A]">
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function KeySetupTabs({ user: initialUser, initialTab }) {
  const [user, setUser] = useState(initialUser || getUser());
  const [tab, setTab] = useState(initialTab || "keys");
  const [summary, setSummary] = useState(null);
  const [topupProvider, setTopupProvider] = useState(null);
  const [redistProvider, setRedistProvider] = useState(null);
  const [redistByokMode, setRedistByokMode] = useState(false);
  const [lockedVendor, setLockedVendor] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await api.get("/me/voucher-summary");
      setSummary(r.data);
    } catch { /* non-fatal — tiles render empty */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  // When a key is added/removed on the Key Setup tab, re-render with the
  // fresh user (so the Trackers tab un-greys live) and refresh balances.
  const onKeyUpdated = (u) => {
    if (u) setUser(u);
    load();
  };

  const byokStatus = summary?.byok_status || {};
  const platformBuckets = summary?.provider_totals_cents || {};
  const byokBalances = summary?.byok_self_balance_cents || {};
  const spent30 = summary?.provider_spent_30d_cents || {};

  // A provider's key is "connected" if either the cached user flag or the
  // server's byok_status says so (user flag updates instantly on save).
  const userFlag = { gemini: !!user?.has_byok, anthropic: !!user?.has_anthropic_byok, openai: !!user?.has_openai_byok };
  const connected = {
    gemini: userFlag.gemini || !!byokStatus.gemini,
    anthropic: userFlag.anthropic || !!byokStatus.anthropic,
    openai: userFlag.openai || !!byokStatus.openai,
  };

  const openRedistribute = (prov, byokModeFlag) => {
    setRedistByokMode(!!byokModeFlag);
    setRedistProvider(prov);
  };

  return (
    <div id="account-key-setup" className="scroll-mt-28" data-testid="account-key-setup">
      {/* Tab strip */}
      <div className="inline-flex items-center gap-1 p-1 rounded-lg bg-[#F0F1EE] border hairline mb-4" data-testid="key-tabs-strip">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              data-testid={`key-tab-${t.id}`}
              className={`px-3 sm:px-4 py-2 rounded-md text-xs sm:text-sm flex items-center gap-1.5 transition-colors ${active ? "bg-white text-[#1C1B1A] shadow-sm" : "text-[#6B6A68] hover:text-[#1C1B1A]"}`}
            >
              <Icon size={14} weight="duotone" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* TAB 1 — Key Setup (existing BYOK panel) */}
      {tab === "keys" && (
        <div data-testid="key-tab-keys-panel">
          <CreatorProviderByokPanel user={user} onUpdated={onKeyUpdated} />
        </div>
      )}

      {/* TAB 2 — Vouchers (all three, red) */}
      {tab === "vouchers" && (
        <div className="bg-white border hairline rounded-xl p-6" data-testid="key-tab-vouchers-panel">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-[#FBE9E1] text-[#C16E5A] flex items-center justify-center shrink-0">
              <Wallet size={20} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-[#1C1B1A]">Voucher balances</h3>
              <p className="text-xs text-[#6B6A68] mt-1 leading-relaxed">
                Only fill up the voucher system if you have <strong>no intention</strong> of using
                bring-your-own-key direct-to-merchant savings. Using My Silo's keys is totally fine —
                it just costs a fraction more per call (e.g. <strong>3¢ vs 1¢</strong>).
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {PROV_IDS.map((p) => (
              <ProviderTrackerTile
                key={p}
                provider={p}
                byok={false}
                bucketCents={platformBuckets[p] || 0}
                spent30Cents={spent30[p] || 0}
                peek={{ context: "dashboard", focusSiloId: null }}
                onTopupClick={(prov) => setTopupProvider(prov)}
                onRedistributeClick={(prov) => openRedistribute(prov, false)}
              />
            ))}
          </div>
        </div>
      )}

      {/* TAB 3 — Key Trackers (all three, green / locked) */}
      {tab === "trackers" && (
        <div className="bg-white border hairline rounded-xl p-6" data-testid="key-tab-trackers-panel">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-10 h-10 rounded-md bg-[#E8EBE5] text-[#4A5D4E] flex items-center justify-center shrink-0">
              <Gauge size={20} weight="duotone" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-medium text-[#1C1B1A]">Your key trackers</h3>
              <p className="text-xs text-[#6B6A68] mt-1 leading-relaxed">
                You may notice one or all of these are greyed out. First open the{" "}
                <button type="button" onClick={() => setTab("keys")} className="underline text-[#4A5D4E] hover:text-[#1C1B1A]" data-testid="trackers-goto-keys">Key Setup</button>{" "}
                tab and connect your direct keys. Tap the <Info size={12} weight="duotone" className="inline mb-0.5" /> icon
                there for tips, instructions, and direct links — it's quick and easy.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {PROV_IDS.map((p) => (
              connected[p] ? (
                <ProviderTrackerTile
                  key={p}
                  provider={p}
                  byok={true}
                  bucketCents={byokBalances[p] || 0}
                  spent30Cents={spent30[p] || 0}
                  peek={{ context: "dashboard", focusSiloId: null }}
                  onRedistributeClick={(prov) => openRedistribute(prov, true)}
                  onTopupClick={(prov) => setTopupProvider(prov)}
                />
              ) : (
                <LockedTrackerTile key={p} provider={p} onClick={(prov) => setLockedVendor(prov)} />
              )
            ))}
          </div>
        </div>
      )}

      <VoucherTopupModal
        open={!!topupProvider}
        onClose={() => setTopupProvider(null)}
        onSuccess={load}
        defaultProvider={topupProvider}
      />
      <RedistributePopup
        open={!!redistProvider}
        onClose={() => setRedistProvider(null)}
        provider={redistProvider}
        byokMode={redistByokMode}
        onSaved={load}
      />
      {lockedVendor && (
        <LockedVendorModal
          provider={lockedVendor}
          onClose={() => setLockedVendor(null)}
          onGoSetup={() => { setLockedVendor(null); setTab("keys"); }}
        />
      )}
    </div>
  );
}
